import React, { forwardRef, RefObject, useState } from 'react';
import {
  MediumCard,
  PercentValue,
  CirculationTitle,
  PlaningFactorTitle,
  UsfBreakdownBox,
  PaddedCardTitle,
} from 'spw-programming-projects/program-summary/single-page-summary/styles';
import { Grid } from '@mui/material';
import {
  asPercentageRounded,
  textOrDash,
  asPercentageFixed,
  formatBigNumber,
  valueOrZero,
  asUsfOrZero,
  numberWithCommasOrDash,
} from 'spw-shared/utils';
import {
  BreakdownBottomRow,
  BreakdownColumn,
  BreakdownRow,
  BreakdownTotalRow,
  Pill,
} from 'spw-programming-projects/program-summary/components/styles';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import UsfBreakdownDialog from 'spw-components/dialogs/total-program/usf-breakdown-dialog';
import { USF_BREAKDOWN_VALUES } from 'spw-components/dialogs/total-program/utils';
import { useSelector } from 'react-redux';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { PlainWorksheet } from '../../../../models/Worksheet';

export interface IUsfBreakdownProps {
  showRsf: boolean;
  planningFactorPercent: number;
  planningFactor: number;
  circulationPercent: number;
  circulation: number;
  totalProgramRSF: number;
  totalProgramUsf: number;
  subTotalProgramUsf: number;
  height?: string;
  classes?: any;
  totalProgramWorksheets?: PlainWorksheet[];
  isTotalProgramScenario: boolean;
  rentableLoadFactor: number;
  rsfLoadFactorPercentage: number;
}

export const UsfBreakdown = forwardRef<HTMLElement, IUsfBreakdownProps>(
  (
    {
      showRsf,
      planningFactorPercent,
      planningFactor,
      circulationPercent,
      circulation,
      totalProgramRSF,
      totalProgramUsf,
      subTotalProgramUsf,
      height,
      classes,
      totalProgramWorksheets,
      isTotalProgramScenario,
      rentableLoadFactor,
      rsfLoadFactorPercentage,
    },
    ref
  ) => {
    const { getUnit, convertArea } = useUnitConversion();
    const measurementSystem = useSelector(selectMeasurementSystem);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [usf, setUsf] = useState<number | string>(0);
    const [usfPercentage, setUsfPercentage] = useState<number | string>('');
    const [valueType, setValueType] = useState<string>('');

    const openDialog = (
      selectedType: string,
      usfValue: string | number,
      usfPercentageValue: string | number
    ) => {
      if (isTotalProgramScenario) {
        setShowDialog(true);
        setValueType(selectedType);
        setUsf(usfValue);
        setUsfPercentage(usfPercentageValue);
      }
    };

    const setProgramTotal = () =>
      openDialog(
        USF_BREAKDOWN_VALUES.PROGRAM_SUBTOTAL,
        asUsfOrZero(subTotalProgramUsf),
        subTotalProgramUsf ? asPercentageRounded(1) : '0%'
      );
    const setCirculation = () =>
      openDialog(
        USF_BREAKDOWN_VALUES.CIRCULATION,
        asUsfOrZero(circulation),
        asPercentageRounded(circulationPercent / 100)
      );
    const setPlanningFactor = () =>
      openDialog(
        USF_BREAKDOWN_VALUES.PLANNING_FACTOR,
        formatBigNumber(Math.round(valueOrZero(planningFactor))),
        asPercentageFixed(planningFactorPercent / 100, false)
      );

    return (
      <>
        {showDialog && isTotalProgramScenario && !window.SPWExport && (
          <UsfBreakdownDialog
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            totalProgramWorksheets={totalProgramWorksheets}
            setProgramTotal={setProgramTotal}
            setCirculation={setCirculation}
            setPlanningFactor={setPlanningFactor}
            usf={usf}
            usfPercentage={usfPercentage}
            valueType={valueType}
            anchorRef={ref}
          />
        )}
        <div ref={ref as RefObject<HTMLDivElement>} style={{ height: '100%' }}>
          <MediumCard
            style={{
              boxSizing: 'border-box',
              height: height || 130,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PaddedCardTitle data-testid="programming_ps_usf_breakdown_title">
              SQUARE{' '}
              {measurementSystem === MeasurementSystem.imperial
                ? 'FOOTAGE'
                : 'METERS'}{' '}
              BREAKDOWN
            </PaddedCardTitle>
            <Grid container style={{ flexGrow: 1 }}>
              <Grid
                item
                xs={12}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <BreakdownRow className={`${classes?.breakdownRow || ''}`}>
                  <UsfBreakdownBox
                    className={isTotalProgramScenario ? 'highlight' : ''}
                    onClick={setProgramTotal}
                  >
                    <BreakdownColumn item xs={8}>
                      Subtotal Program {getUnit('area')}
                    </BreakdownColumn>
                    <BreakdownColumn
                      item
                      xs={4}
                      className="right"
                      data-testid="programming_ps_usf_breakdown_subTotalProgramUsf"
                    >
                      {numberWithCommasOrDash(convertArea(subTotalProgramUsf))}
                    </BreakdownColumn>
                  </UsfBreakdownBox>
                </BreakdownRow>
                <BreakdownRow className={`${classes?.breakdownRow || ''}`}>
                  <UsfBreakdownBox
                    className={isTotalProgramScenario ? 'highlight' : ''}
                    onClick={setCirculation}
                  >
                    <BreakdownColumn item xs={8}>
                      <CirculationTitle>
                        <Pill className="margin-left" color="transparent">
                          Circulation @
                          <PercentValue data-testid="programming_ps_usf_breakdown_circulationPercent">
                            {circulationPercent
                              ? asPercentageRounded(circulationPercent / 100)
                              : textOrDash(circulationPercent)}
                          </PercentValue>
                        </Pill>
                      </CirculationTitle>
                    </BreakdownColumn>
                    <BreakdownColumn
                      item
                      xs={4}
                      className="right"
                      data-testid="programming_ps_usf_breakdown_circulation"
                    >
                      {numberWithCommasOrDash(convertArea(circulation))}
                    </BreakdownColumn>
                  </UsfBreakdownBox>
                </BreakdownRow>

                <BreakdownRow
                  className={`${classes?.breakdownRow || ''} ${
                    classes?.last || ''
                  } last`}
                >
                  <UsfBreakdownBox
                    className={isTotalProgramScenario ? 'highlight' : ''}
                    onClick={setPlanningFactor}
                  >
                    <BreakdownColumn item xs={8}>
                      <PlaningFactorTitle>
                        <Pill className="margin-left" color="transparent">
                          Planning Factor @
                          <PercentValue data-testid="programming_ps_usf_breakdown_planningFactorPercent">
                            {planningFactorPercent
                              ? asPercentageRounded(planningFactorPercent / 100)
                              : textOrDash(planningFactorPercent)}
                          </PercentValue>
                        </Pill>
                      </PlaningFactorTitle>
                    </BreakdownColumn>
                    <BreakdownColumn
                      item
                      xs={4}
                      className="right"
                      data-testid="programming_ps_usf_breakdown_planningFactor"
                    >
                      {numberWithCommasOrDash(convertArea(planningFactor))}
                    </BreakdownColumn>
                  </UsfBreakdownBox>
                </BreakdownRow>

                <BreakdownTotalRow>
                  <BreakdownColumn item xs={8}>
                    Total Program {getUnit('area')}
                  </BreakdownColumn>
                  <BreakdownColumn
                    item
                    xs={4}
                    className="right"
                    data-testid="programming_ps_usf_breakdown_totalProgramUSF"
                  >
                    {numberWithCommasOrDash(convertArea(totalProgramUsf))}
                  </BreakdownColumn>
                </BreakdownTotalRow>

                {showRsf && (
                  <div style={{ marginTop: 'auto' }}>
                    <BreakdownRow
                      className={`${
                        classes?.breakdownRow || ''
                      } bottom-and-separated`}
                    >
                      <BreakdownColumn item xs={8}>
                        <Pill className="margin-left" color="transparent">
                          Rentable Load Factor @
                          <PercentValue>
                            {asPercentageRounded(
                              rsfLoadFactorPercentage / 100,
                              true
                            )}
                          </PercentValue>
                        </Pill>
                      </BreakdownColumn>
                      <BreakdownColumn item xs={4} className="right">
                        {numberWithCommasOrDash(
                          convertArea(rentableLoadFactor)
                        )}
                      </BreakdownColumn>
                    </BreakdownRow>
                    <BreakdownBottomRow>
                      <BreakdownColumn item xs={8}>
                        <Pill className="margin-left" color="transparent">
                          Total Program {getUnit('rentable-area')}
                        </Pill>
                      </BreakdownColumn>
                      <BreakdownColumn item xs={4} className="right">
                        {numberWithCommasOrDash(
                          rentableLoadFactor &&
                            String(rentableLoadFactor) !== '0'
                            ? convertArea(totalProgramRSF)
                            : 0
                        )}
                      </BreakdownColumn>
                    </BreakdownBottomRow>
                  </div>
                )}
              </Grid>
            </Grid>
          </MediumCard>
        </div>
      </>
    );
  }
);
